<template>
  <div class="wizard-body columns content-box small-9">
    <div class="header-actions">
      <div>
        <h4>{{ $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.STAGES.TITLE') }}</h4>
        <p class="header-subtitle">
          {{ $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.STAGES.DESC') }}
        </p>
        <p class="limit-subtitle">
          {{
            $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.STAGES.LIMIT_INFO', {
              min: stageLimits.MIN,
              max: stageLimits.MAX,
            })
          }}
        </p>
      </div>
      <woot-button
        color-scheme="success"
        :disabled="!canCreateMoreStages"
        @click="openCreateModal"
      >
        {{ $t('OPPORTUNITY_PIPELINES.STAGES.NEW_BUTTON') }}
      </woot-button>
    </div>

    <empty-state
      v-if="!isLoading && !stages.length"
      :image-src="emptyStagesImage"
      :title="$t('OPPORTUNITY_PIPELINES.STAGES.EMPTY_TITLE')"
      :message="$t('OPPORTUNITY_PIPELINES.STAGES.EMPTY_MESSAGE')"
    />

    <woot-loading-state
      v-if="isLoading"
      :message="$t('OPPORTUNITY_PIPELINES.STAGES.LOADING')"
    />

    <draggable
      v-if="!isLoading && stages.length"
      v-model="stages"
      class="stages-list"
      handle=".drag-handle"
      @end="persistOrder"
    >
      <div v-for="stage in stages" :key="stage.id" class="stage-card">
        <div class="stage-info">
          <span class="drag-handle">
            <fluent-icon icon="drag" />
          </span>
          <span class="stage-color" :style="{ backgroundColor: stage.color }" />
          <div>
            <p class="stage-name">{{ stage.name }}</p>
            <p class="stage-description">{{ stage.description || '-' }}</p>
            <p class="stage-sla">
              {{
                $t('OPPORTUNITY_PIPELINES.STAGES.SLA_LABEL', {
                  days: stage.sla_days,
                })
              }}
            </p>
          </div>
        </div>

        <div class="stage-actions">
          <woot-switch
            :value="stage.status === 'active'"
            @input="toggleStatus(stage, $event)"
          />
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            icon="edit"
            size="tiny"
            class-names="grey-btn"
            @click="openEditModal(stage)"
          />
          <woot-button
            v-if="canDeleteStage(stage)"
            variant="smooth"
            color-scheme="alert"
            size="tiny"
            icon="dismiss-circle"
            class-names="grey-btn"
            @click="openDeleteModal(stage)"
          />
        </div>
      </div>
    </draggable>

    <div class="button-row">
      <woot-button type="button" color-scheme="secondary" @click="goToPrevious">
        {{ $t('OPPORTUNITY_PIPELINES.ACTIONS.BACK') }}
      </woot-button>
      <woot-button :disabled="!hasMinimumStages" @click="goToNext">
        {{ $t('OPPORTUNITY_PIPELINES.CREATE_FLOW.STAGES.SUBMIT') }}
      </woot-button>
    </div>

    <woot-modal :show.sync="showStageModal" :on-close="closeModal">
      <stage-modal
        :stage="selectedStage"
        :is-saving="isSaving"
        @submit="saveStage"
        @close="closeModal"
      />
    </woot-modal>

    <woot-delete-modal
      v-if="showDeleteModal"
      :show.sync="showDeleteModal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeleteStage"
      :title="$t('OPPORTUNITY_PIPELINES.STAGES.DELETE.CONFIRM.TITLE')"
      :message="$t('OPPORTUNITY_PIPELINES.STAGES.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="$t('OPPORTUNITY_PIPELINES.STAGES.DELETE.CONFIRM.YES')"
      :reject-text="$t('OPPORTUNITY_PIPELINES.ACTIONS.CANCEL')"
    />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import alertMixin from 'shared/mixins/alertMixin';
import EmptyState from '../../../../../components/widgets/EmptyState';
import router from '../../../../index';
import StageModal from './StageModal';
import { patchDraft, readDraft } from './draftState';
import emptyStagesImage from '../../../../../assets/images/opportunity-pipelines/empty-stages.svg';
import { STAGE_LIMITS } from './config';
import { sortStagesByPosition } from '../../../../../helper/opportunityPipelineHelper';

export default {
  components: {
    draggable,
    EmptyState,
    StageModal,
  },
  mixins: [alertMixin],
  data() {
    return {
      isLoading: false,
      isSaving: false,
      stages: [],
      showStageModal: false,
      showDeleteModal: false,
      selectedStage: {},
      emptyStagesImage,
    };
  },
  computed: {
    pipelineId() {
      return this.$route.params.pipelineId;
    },
    isNewDraftFlow() {
      return this.$route.name === 'settings_opportunity_pipelines_new_stages';
    },
    stageLimits() {
      return STAGE_LIMITS;
    },
    canCreateMoreStages() {
      return this.stages.length < this.stageLimits.MAX;
    },
    hasMinimumStages() {
      return this.stages.length >= this.stageLimits.MIN;
    },
    canDeleteStage() {
      return stage => {
        const hasLinkedOpportunities =
          Number(stage.opportunities_count || 0) > 0;
        const hasMinimumRemainingStages =
          this.stages.length > this.stageLimits.MIN;

        return hasMinimumRemainingStages && !hasLinkedOpportunities;
      };
    },
  },
  mounted() {
    this.fetchStages();
  },
  methods: {
    async fetchStages() {
      if (this.isNewDraftFlow) {
        const draft = readDraft();
        this.stages = sortStagesByPosition(draft.stages || []);
        return;
      }

      this.isLoading = true;
      try {
        const stages = await this.$store.dispatch(
          'opportunityPipelines/getStages',
          this.pipelineId
        );
        this.stages = sortStagesByPosition(stages || []);
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.FETCH_STAGES'));
      } finally {
        this.isLoading = false;
      }
    },
    openCreateModal() {
      if (!this.canCreateMoreStages) {
        this.showAlert(
          this.$t('OPPORTUNITY_PIPELINES.ERRORS.STAGES_MAX_LIMIT', {
            max: this.stageLimits.MAX,
          })
        );
        return;
      }

      this.selectedStage = {};
      this.showStageModal = true;
    },
    openEditModal(stage) {
      this.selectedStage = { ...stage };
      this.showStageModal = true;
    },
    closeModal() {
      this.showStageModal = false;
      this.selectedStage = {};
    },
    openDeleteModal(stage) {
      if (!this.canDeleteStage(stage)) {
        this.showAlert(
          this.$t('OPPORTUNITY_PIPELINES.ERRORS.STAGES_MIN_LIMIT', {
            min: this.stageLimits.MIN,
          })
        );
        return;
      }

      this.selectedStage = { ...stage };
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.selectedStage = {};
    },
    async confirmDeleteStage() {
      if (!this.selectedStage.id) {
        return;
      }

      if (!this.canDeleteStage(this.selectedStage)) {
        this.showAlert(
          this.$t('OPPORTUNITY_PIPELINES.ERRORS.STAGES_MIN_LIMIT', {
            min: this.stageLimits.MIN,
          })
        );
        return;
      }

      const stageId = this.selectedStage.id;

      try {
        if (this.isNewDraftFlow) {
          this.stages = this.stages
            .filter(stage => stage.id !== stageId)
            .map((stage, index) => ({
              ...stage,
              position: index + 1,
            }));

          patchDraft({ stages: [...this.stages] });
        } else {
          await this.$store.dispatch('opportunityPipelines/removeStage', {
            pipelineId: this.pipelineId,
            stageId,
          });
          await this.fetchStages();
        }
        this.closeDeleteModal();
      } catch (error) {
        const backendMessage = error?.response?.data?.message;
        this.showAlert(
          backendMessage || this.$t('OPPORTUNITY_PIPELINES.ERRORS.DELETE_STAGE')
        );
      }
    },
    async saveStage(stage) {
      if (this.isNewDraftFlow) {
        if (this.selectedStage.id) {
          this.stages = this.stages.map(item =>
            item.id === this.selectedStage.id
              ? {
                  ...item,
                  ...stage,
                }
              : item
          );
        } else {
          if (!this.canCreateMoreStages) {
            this.showAlert(
              this.$t('OPPORTUNITY_PIPELINES.ERRORS.STAGES_MAX_LIMIT', {
                max: this.stageLimits.MAX,
              })
            );
            return;
          }

          const nextId =
            this.stages.reduce((max, item) => Math.max(max, item.id || 0), 0) +
            1;
          this.stages = [
            ...this.stages,
            {
              ...stage,
              id: nextId,
              status: stage.status || 'active',
              position: this.stages.length + 1,
            },
          ];
        }

        patchDraft({ stages: [...this.stages] });
        this.closeModal();
        return;
      }

      this.isSaving = true;
      try {
        if (this.selectedStage.id) {
          await this.$store.dispatch('opportunityPipelines/updateStage', {
            pipelineId: this.pipelineId,
            stageId: this.selectedStage.id,
            payload: stage,
          });
        } else {
          if (!this.canCreateMoreStages) {
            this.showAlert(
              this.$t('OPPORTUNITY_PIPELINES.ERRORS.STAGES_MAX_LIMIT', {
                max: this.stageLimits.MAX,
              })
            );
            return;
          }

          await this.$store.dispatch('opportunityPipelines/createStage', {
            pipelineId: this.pipelineId,
            payload: { ...stage, position: this.stages.length + 1 },
          });
        }
        await this.fetchStages();
        this.closeModal();
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.SAVE_STAGE'));
      } finally {
        this.isSaving = false;
      }
    },
    async toggleStatus(stage, checked) {
      if (this.isNewDraftFlow) {
        stage.status = checked ? 'active' : 'inactive';
        patchDraft({ stages: [...this.stages] });
        return;
      }

      try {
        await this.$store.dispatch('opportunityPipelines/updateStageStatus', {
          pipelineId: this.pipelineId,
          stageId: stage.id,
          status: checked ? 'active' : 'inactive',
        });
        stage.status = checked ? 'active' : 'inactive';
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.TOGGLE_STAGE'));
      }
    },
    async persistOrder() {
      if (this.isNewDraftFlow) {
        this.stages = this.stages.map((stage, index) => ({
          ...stage,
          position: index + 1,
        }));
        patchDraft({ stages: [...this.stages] });
        return;
      }

      const orderedStages = this.stages.map((stage, index) => ({
        id: stage.id,
        position: index + 1,
      }));

      try {
        await this.$store.dispatch('opportunityPipelines/reorderStages', {
          pipelineId: this.pipelineId,
          stages: orderedStages,
        });
        this.stages = this.stages.map((stage, index) => ({
          ...stage,
          position: index + 1,
        }));
      } catch (error) {
        this.showAlert(this.$t('OPPORTUNITY_PIPELINES.ERRORS.REORDER_STAGES'));
        this.fetchStages();
      }
    },
    goToNext() {
      if (!this.hasMinimumStages) {
        this.showAlert(
          this.$t('OPPORTUNITY_PIPELINES.ERRORS.STAGES_MIN_LIMIT', {
            min: this.stageLimits.MIN,
          })
        );
        return;
      }

      if (this.isNewDraftFlow) {
        patchDraft({ stages: [...this.stages] });
        router.replace({
          name: 'settings_opportunity_pipelines_new_loss_reasons',
        });
        return;
      }

      router.replace({
        name: 'settings_opportunity_pipelines_loss_reasons',
        params: { pipelineId: this.pipelineId },
      });
    },
    goToPrevious() {
      if (this.isNewDraftFlow) {
        patchDraft({ stages: [...this.stages] });
        router.replace({ name: 'settings_opportunity_pipelines_new_inboxes' });
        return;
      }

      router.replace({
        name: 'settings_opportunity_pipelines_inboxes',
        params: { pipelineId: this.pipelineId },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.header-actions {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--space-small);
}

.header-subtitle {
  color: var(--s-700);
  font-size: var(--font-size-small);
  margin: 0;
}

.limit-subtitle {
  color: var(--s-700);
  font-size: var(--font-size-mini);
  margin: var(--space-micro) 0 0;
}

.stages-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
  margin-top: var(--space-small);
}

.stage-card {
  align-items: center;
  background: var(--white);
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-medium);
  box-shadow: 0 0.6rem 1.6rem rgba(16, 24, 40, 0.04);
  display: flex;
  justify-content: space-between;
  padding: var(--space-normal);
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}

.stage-card:hover {
  border-color: var(--s-200);
  box-shadow: 0 1rem 2.2rem rgba(16, 24, 40, 0.08);
}

.stage-info {
  align-items: center;
  display: flex;
  gap: var(--space-small);
}

.drag-handle {
  color: var(--s-700);
  cursor: move;
}

.stage-color {
  border-radius: 999px;
  display: inline-block;
  height: 1.4rem;
  width: 1.4rem;
}

.stage-name {
  font-weight: var(--font-weight-medium);
  margin: 0;
}

.stage-description,
.stage-sla {
  color: var(--s-700);
  font-size: var(--font-size-mini);
  margin: 0;
}

.stage-actions {
  align-items: center;
  display: flex;
  gap: var(--space-smaller);
}

.button-row {
  display: flex;
  gap: var(--space-small);
  justify-content: flex-end;
  margin-top: var(--space-small);
}

@media screen and (max-width: 768px) {
  .header-actions {
    flex-direction: column;
    gap: var(--space-small);
  }

  .stage-card {
    align-items: flex-start;
    flex-direction: column;
    gap: var(--space-small);
  }

  .stage-actions {
    justify-content: flex-end;
    width: 100%;
  }
}
</style>
